import React, { useContext, useState } from "react";
import {
  Alert,
  Backdrop,
  Box,
  Button,
  CircularProgress,
  IconButton,
  Container,
  TextField,
  InputAdornment,
  Typography,
} from "@mui/material";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import { auth } from "../../firebase";
import bkstApi from "../../api";
import { setDataInLocalStorage, getDataFromLocalStorage, getSharedDeviceUid } from "../util";
import { useNavigate } from "react-router";
import LogoutIcon from "@mui/icons-material/Logout";
import { UserContext } from "../../context/UserContext";
import { BAKER_PIC_FOLDER } from "../constants";

export default function LoginWithAuthCode(props) {
  const navigate = useNavigate();
  const { user } = useContext(UserContext);

  const [code, setCode] = useState("");
  const [reveal, setReveal] = React.useState(false);
  const [logoFailed, setLogoFailed] = useState(false);

  const [error, setError] = useState("");
  const [processing, setProcessing] = useState(false);

  const bizName = user?.profile?.bizName || "";
  const logoSrc = user?.acctId ? `${BAKER_PIC_FOLDER}/${user.acctId}/logo400.webp` : "";
  const showLogo = Boolean(logoSrc) && !logoFailed;

  const login = (code) => {
    if (processing) {
      return;
    }
    if (!code || String(code).length !== 4) {
      setError("Incorrect Access Code");
      setCode("");
      return;
    }

    setProcessing(true);
    setError("");

    const sharedDeviceUid = getSharedDeviceUid();
    const mode = getDataFromLocalStorage("pos") ? "pos" : getDataFromLocalStorage("kds") ? "kds" : "";
    bkstApi
      .post(
        `/login-with-code?code=${code}&sharedDeviceUid=${sharedDeviceUid}${
          mode ? `&mode=${mode}` : ""
        }`
      )
      .then((res) => {
        const token = res.data?.token;
        if (!token) {
          setError("Incorrect Access Code");
          setCode("");
          setProcessing(false);
          return;
        }

        auth
          .signInWithCustomToken(token)
          .then((userCredential) => {
            var staffUser = userCredential.user;
            setError("");
            setCode("");

            if (getDataFromLocalStorage("pos")) {
              setDataInLocalStorage("pos-user", { uid: staffUser.uid });
              navigate("/");
            } else if (getDataFromLocalStorage("kds")) {
              setDataInLocalStorage("kds-user", { uid: staffUser.uid });
              navigate("/production/tracker");
            } else {
              navigate("/");
            }
          })
          .catch(() => {
            setError("Incorrect Access Code");
            setCode("");
            setProcessing(false);
          });
      })
      .catch((err) => {
        setError(err.response?.data?.message || "Incorrect Access Code");
        setCode("");
        setProcessing(false);
      });
  };

  const clearMode = () => {
    auth.signOut().then(() => {
      localStorage.removeItem("pos");
      localStorage.removeItem("pos-user");
      localStorage.removeItem("kds");
      localStorage.removeItem("kds-user");

      window.location.reload();
    });
  };

  const getButton = (key) => {
    return (
      <Button
        variant="contained"
        fullWidth
        disabled={processing}
        onClick={(e) => {
          if (key === "⌫") {
            setCode(code.slice(0, -1));
          } else if (key === "↵") {
            login(code);
          } else {
            const newCode = `${code}${key}`;
            setCode(newCode);
            if (newCode.length === 4) {
              login(newCode);
            }
          }
        }}
        sx={{ height: "6rem", fontWeight: "700", fontSize: "2rem", backgroundColor: "#111" }}
      >
        {key}
      </Button>
    );
  };

  return (
    <Box sx={{ backgroundColor: "#333", minHeight: "100vh", position: "relative" }}>
      <Box
        sx={{
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          px: 4,
          pb: "18vh",
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "flex-start",
            justifyContent: "center",
            gap: 8,
          }}
        >
        {(showLogo || bizName) && (
          <Box sx={{ textAlign: "center", flexShrink: 0 }}>
            {showLogo && (
              <Box
                component="img"
                key={user.acctId}
                alt={bizName || "Bakery"}
                src={logoSrc}
                onError={() => setLogoFailed(true)}
                sx={{
                  width: 180,
                  height: 180,
                  objectFit: "contain",
                  backgroundColor: "#fff",
                  borderRadius: "12px",
                  p: 1.5,
                }}
              />
            )}
            {bizName && (
              <Typography
                variant="h4"
                sx={{ color: "#fff", fontWeight: 700, mt: showLogo ? 2 : 0 }}
              >
                {bizName}
              </Typography>
            )}
          </Box>
        )}
        <Box sx={{ flexShrink: 0, width: 444 }}>
          <Container maxWidth="xs" disableGutters>
            <form
              onSubmit={(e) => {
                e.preventDefault();
                login(code);
              }}
            >
              <Box>
                {error && (
                  <Box mb={2}>
                    <Alert severity="error">{error}</Alert>
                  </Box>
                )}
                <TextField
                  value={code}
                  color="secondary"
                  name="code"
                  variant="outlined"
                  fullWidth
                  id="code"
                  label=""
                  placeholder="Enter your 4-digit access code"
                  type={reveal && code.length > 0 ? "text" : "password"}
                  disabled={processing}
                  onChange={(e) => setCode(e.target.value)}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          aria-label="toggle password visibility"
                          onClick={() => setReveal(!reveal)}
                          edge="end"
                        >
                          {reveal ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                  inputProps={{
                    style: {
                      textAlign: "center",
                      fontSize: code ? "2rem" : "1rem",
                      letterSpacing: code ? "1rem" : "normal",
                      padding: code ? 0 : "12px 0",
                    },
                  }}
                  sx={{
                    backgroundColor: "#777",
                    borderRadius: "5px",
                    "& input::placeholder": {
                      opacity: 0.85,
                      color: "#fff",
                    },
                  }}
                />
                <Box>
                  <Box my={2} sx={{ display: "flex", gap: "2rem", justifyContent: "space-between" }}>
                    {[1, 2, 3].map((num) => getButton(num))}
                  </Box>
                  <Box my={2} sx={{ display: "flex", gap: "2rem", justifyContent: "space-between" }}>
                    {[4, 5, 6].map((num) => getButton(num))}
                  </Box>
                  <Box my={2} sx={{ display: "flex", gap: "2rem", justifyContent: "space-between" }}>
                    {[7, 8, 9].map((num) => getButton(num))}
                  </Box>
                  <Box my={2} sx={{ display: "flex", gap: "2rem", justifyContent: "space-between" }}>
                    {["⌫", 0, "↵"].map((num) => getButton(num))}
                  </Box>
                </Box>
              </Box>
            </form>
          </Container>
        </Box>
        </Box>
      </Box>
      <Box sx={{ position: "absolute", bottom: 8, left: 8 }}>
        <IconButton size="small" sx={{ color: "#777" }} onClick={clearMode} disabled={processing}>
          <LogoutIcon fontSize="small" />
        </IconButton>
      </Box>
      <Backdrop open={processing} sx={{ zIndex: 1300, color: "#fff" }}>
        <CircularProgress color="inherit" />
      </Backdrop>
    </Box>
  );
}
